import type { PlatformPolicyConfig } from '../../lib/types';
import { getArchetypePolarity, getArchetypeWeights, type ArchetypeKey, type SubScoreKey, RAJA_CRITERIA_KEYS } from './weights';
import { getStage, type AdhikaraStage } from './stage';

export interface SubScores {
  hridaya: number | null | undefined;
  vak: number | null | undefined;
  jnana: number | null | undefined;
  karma: number | null | undefined;
  phala: number | null | undefined;
}

export interface AdhikaraTraceItem {
  key: SubScoreKey;
  value: number;
  weight: number;
  polarity: string;
  contribution: number;
}

export interface AdhikaraComputation {
  score: number;
  stage: AdhikaraStage | null;
  trace: AdhikaraTraceItem[];
  polarityProfile: {
    hridayaPolarity: string;
    vakPolarity: string;
    butterBladeMirror: boolean;
  };
}

export interface AcharyaQualificationInput {
  criteria: {
    vriddha?: boolean;
    alolupa?: boolean;
    atmavant?: boolean;
    dambhahin?: boolean;
    samyagvinita?: boolean;
    mridu?: boolean;
    witnessApproved?: boolean;
  };
  adhikaraScore: number | null | undefined;
}

export interface RajaQualificationInput {
  criteria: Partial<Record<typeof RAJA_CRITERIA_KEYS[number], boolean>>;
}

export interface SangatiScoreEvent {
  type: 'SANGATI_SCORE';
  reason: string;
  uid: string;
  archetype: ArchetypeKey;
  score: number;
  stage: AdhikaraStage | null;
  trace: AdhikaraTraceItem[];
}

function clampScore(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(9, Math.max(0, Math.round(value)));
}

function normalize(value: number | null | undefined): number {
  if (!Number.isFinite(value as number)) return 0;
  return Math.min(9, Math.max(0, Number(value)));
}

function getPolarityLabel(archetype: ArchetypeKey, key: SubScoreKey, policy?: PlatformPolicyConfig | null): string {
  const polarity = getArchetypePolarity(policy, archetype);
  if (key === 'hridaya') return polarity.hridayaPolarity;
  if (key === 'vak') return polarity.vakPolarity;
  return 'NEUTRAL';
}

function isButterBladeMirror(archetype: ArchetypeKey): boolean {
  return archetype === 'BRAHMIN' || archetype === 'KSHATRIYA';
}

export function computeAdhikara(scores: SubScores, archetype: ArchetypeKey, policy?: PlatformPolicyConfig | null): AdhikaraComputation {
  const weights = getArchetypeWeights(policy, archetype);
  const trace = (['hridaya', 'vak', 'jnana', 'karma', 'phala'] as const).map((key) => {
    const value = normalize(scores[key]);
    const weight = weights[key];
    return {
      key,
      value,
      weight,
      polarity: getPolarityLabel(archetype, key, policy),
      contribution: value * weight,
    } satisfies AdhikaraTraceItem;
  });

  const raw = trace.reduce((sum, item) => sum + item.contribution, 0);
  const score = clampScore(raw);
  return {
    score,
    stage: getStage(score, policy),
    trace,
    polarityProfile: {
      hridayaPolarity: getPolarityLabel(archetype, 'hridaya', policy),
      vakPolarity: getPolarityLabel(archetype, 'vak', policy),
      butterBladeMirror: isButterBladeMirror(archetype),
    },
  };
}

export function isQualifiedAcharya(candidate: AcharyaQualificationInput): boolean {
  return (
    candidate.criteria.vriddha === true &&
    candidate.criteria.alolupa === true &&
    candidate.criteria.atmavant === true &&
    candidate.criteria.dambhahin === true &&
    candidate.criteria.samyagvinita === true &&
    candidate.criteria.mridu === true &&
    candidate.criteria.witnessApproved === true &&
    Number.isFinite(candidate.adhikaraScore as number) &&
    Number(candidate.adhikaraScore) >= 7
  );
}

export function isQualifiedRaja(candidate: RajaQualificationInput): boolean {
  return RAJA_CRITERIA_KEYS.every((key) => candidate.criteria[key] === true);
}

export function createSangatiScoreEvent(
  payload: Omit<SangatiScoreEvent, 'type'>,
): SangatiScoreEvent {
  return {
    type: 'SANGATI_SCORE',
    ...payload,
  };
}
